import Backdrop from 'components/backdrop';
import FilterDialog from 'components/filter-dialog';
import Hero from 'components/hero';
import Layout from 'components/layout';
import LocationMap from 'components/location-map';
import SEO from 'components/seo';
import ServicesFilter, { Filter } from 'components/services-filter';
import SpecialistList from 'components/specialist-list';
import { Button, Text } from 'components/ui';
import { GetStaticProps, NextPage } from 'next';
import { useMemo, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { REVALIDATE_TIME } from 'settings/config';
import styled from 'styled-components';
import { breakpoints } from 'styles/theme';
import { __DEV__ } from 'utils/helpers';
import {
  SharedData,
  sharedDataQuery,
  specialistsQuery,
  SpecialistTypes,
  specialistTypesQuery,
  SpecialistWithExpandedRefs,
} from 'utils/queries';
import { usePreviewSubscription } from 'utils/sanity';
import { getClient } from 'utils/sanity-server';
import { ReactComponent as FilterIcon } from '../images/filter.svg';

interface ServicesPageProps {
  sharedData?: SharedData;
  specialistTypesData?: SpecialistTypes;
  allSpecialistsData?: SpecialistWithExpandedRefs[];
  preview?: boolean;
}

// Filter the specialists according to the active filter
function filterSpecialists(
  filter: Filter,
  specialists: SpecialistWithExpandedRefs[]
) {
  // Find matching services within the active filter for the given specialist
  const hasMatchingServices = (specialist: SpecialistWithExpandedRefs) =>
    (
      specialist.services?.filter((service) =>
        filter.services.includes(service.service?._id || '')
      ) || []
    ).length > 0;

  // Location only
  if (filter.location !== 'all' && filter.specialistType === 'all') {
    return specialists.filter(
      (specialist) => specialist.location?._id === filter.location
    );
  }

  // Type only
  if (filter.location === 'all' && filter.specialistType !== 'all') {
    return specialists.filter(
      (specialist) =>
        specialist.specialistType?._id === filter.specialistType &&
        hasMatchingServices(specialist)
    );
  }

  // All filters
  if (filter.location !== 'all' && filter.specialistType !== 'all') {
    return specialists.filter(
      (specialist) =>
        specialist.location?._id === filter.location &&
        specialist.specialistType?._id === filter.specialistType &&
        hasMatchingServices(specialist)
    );
  }

  // No filters
  return specialists;
}

const defaultValues: Filter = {
  location: 'all',
  specialistType: 'all',
  services: [],
};

const Wrapper = styled(Backdrop)`
  padding-right: 0;
  padding-left: 0;

  @media screen and (min-width: ${breakpoints.medium}) {
    display: grid;
    grid-template-columns: minmax(240px, 1fr) 4fr;
    gap: var(--space-4);
  }

  @media screen and (min-width: ${breakpoints.large}) {
    gap: var(--space-6);
  }

  @media screen and (min-width: ${breakpoints.xLarge}) {
    gap: var(--space-8);
  }
`;

const ColumnTitle = styled(Text).attrs({ fontWeight: 'bold' })`
  display: flex;
  align-items: center;
  min-height: 24px;
  min-height: 74px;
  padding: var(--space-2);
  line-height: var(--line-height-small);
  text-transform: uppercase;
  border-bottom: 1px solid var(--color-grey200);

  svg {
    margin-right: var(--space-1);
  }

  @media screen and (min-width: ${breakpoints.small}) {
    padding-right: 0;
    padding-left: 0;
  }

  @media screen and (min-width: ${breakpoints.medium}) {
    margin-bottom: var(--space-5);
    padding: 0;
    padding-bottom: var(--space-5);
  }
`;

const FilterWrapper = styled.aside`
  display: none;

  @media screen and (min-width: ${breakpoints.medium}) {
    display: block;
  }
`;

const ListWrapper = styled.div`
  display: grid;

  @media screen and (min-width: ${breakpoints.small}) {
    gap: var(--space-4);
  }

  @media screen and (min-width: ${breakpoints.medium}) {
    --padding: var(--space-4);

    display: block;
    gap: 0;
    padding-bottom: var(--padding);
    padding-left: var(--padding);
    border-bottom: 1px solid var(--color-grey200);
    border-left: 1px solid var(--color-grey200);
  }

  @media screen and (min-width: ${breakpoints.large}) {
    --padding: var(--space-6);
  }

  @media screen and (min-width: ${breakpoints.xLarge}) {
    --padding: var(--space-8);
  }

  ${ColumnTitle} {
    margin-top: calc(var(--space-4) * -1);

    @media screen and (min-width: ${breakpoints.small}) {
      margin-top: calc(var(--space-6) * -1);
    }

    @media screen and (min-width: ${breakpoints.medium}) {
      margin-top: 0;
    }
  }
`;

const FilterButton = styled(Button)`
  display: flex;
  align-items: center;
  margin-left: auto;

  svg {
    margin-right: var(--space-0);
  }

  svg * {
    fill: currentColor;
  }

  @media screen and (min-width: ${breakpoints.medium}) {
    display: none;
  }
`;

const ServicesPage: NextPage<ServicesPageProps> = ({
  sharedData,
  specialistTypesData,
  allSpecialistsData,
  preview,
}) => {
  // Set up form state
  const methods = useForm<Filter>({
    defaultValues,
  });

  // The active set of filters applied
  const [activeFilter, setActiveFilter] = useState<Filter>(defaultValues);

  // The filter dialog
  const [dialogVisible, setDialogVisible] = useState(false);

  const { data: shared } = usePreviewSubscription(sharedDataQuery, {
    initialData: sharedData,
    enabled: preview,
  });

  const { data: specialistTypes } = usePreviewSubscription(
    specialistTypesQuery,
    {
      initialData: specialistTypesData,
      enabled: preview,
    }
  );

  const { data: allSpecialists } = usePreviewSubscription(specialistsQuery, {
    initialData: allSpecialistsData,
    enabled: preview,
  });

  // Specialists data with filtering applied
  const filteredSpecialists = useMemo(
    () => allSpecialists && filterSpecialists(activeFilter, allSpecialists),
    [activeFilter, allSpecialists]
  );

  // Clear the filter form and filter state
  const handleClear = () => {
    methods.reset();
    setActiveFilter(defaultValues);
  };

  return (
    <Layout preview={preview} spaceLinks={shared.spaceInfoPaths}>
      <SEO title='Services' />
      <Hero
        title='Our services'
        description='Welcome to our team of professionals.'
        backgroundColor='pink'
        flourishTopLeft='small'
      />
      {shared.locations && specialistTypes && filteredSpecialists && (
        <Wrapper>
          <FormProvider {...methods}>
            <FilterWrapper>
              <ColumnTitle>
                <FilterIcon /> Filter
              </ColumnTitle>
              {!dialogVisible && (
                <ServicesFilter
                  locations={shared.locations}
                  specialistTypes={specialistTypes}
                  onChange={(filters) => setActiveFilter(filters)}
                />
              )}
            </FilterWrapper>
            <ListWrapper>
              <ColumnTitle>
                Salonians{' '}
                <FilterButton onClick={() => setDialogVisible(true)}>
                  <FilterIcon /> Filter
                </FilterButton>
              </ColumnTitle>
              <SpecialistList specialists={filteredSpecialists} />
            </ListWrapper>
            {dialogVisible && (
              <FilterDialog
                onClose={() => setDialogVisible(false)}
                onClear={handleClear}
              >
                <ServicesFilter
                  locations={shared.locations}
                  specialistTypes={specialistTypes}
                  onChange={(filters) => setActiveFilter(filters)}
                />
              </FilterDialog>
            )}
          </FormProvider>
        </Wrapper>
      )}
      <LocationMap locations={shared.locations} />
    </Layout>
  );
};

export default ServicesPage;

export const getStaticProps: GetStaticProps<ServicesPageProps> = async ({
  preview = __DEV__,
}) => {
  const sharedData = await getClient(preview).fetch<SharedData>(
    sharedDataQuery
  );

  const specialistTypesData = await getClient(preview).fetch<SpecialistTypes>(
    specialistTypesQuery
  );

  const allSpecialistsData = await getClient(preview).fetch<
    SpecialistWithExpandedRefs[]
  >(specialistsQuery);

  return {
    props: {
      sharedData,
      specialistTypesData,
      allSpecialistsData,
      preview,
    },
    revalidate: REVALIDATE_TIME,
  };
};
